import React, { useEffect } from 'react';
import { Icon, type IconName } from '../primitives/Icon';
import { Platform, Pressable } from 'react-native';
import { Text, XStack, YStack } from 'tamagui';
import { useThemeColors } from '../hooks/useThemeColors';
import { useSafeAreaTokens } from '../hooks/useSafeAreaTokens';
import { getPolaroidShadowStyles } from '../theme/shadowStyles';
import { IconButton } from '../primitives/IconButton';
import type { ThemeColors } from '../theme/colors';
import { useToastStore, type ActiveToast } from './toast-store';
import { pushEscapeHandler } from '../utils/escapeStack';
import type { ToastVariant } from './types';

interface VariantSpec {
  icon: IconName;
  accent: keyof ThemeColors;
  role: 'alert' | 'status';
}

// Per-variant spec table — one row per variant beats three parallel
// dictionaries that have to be kept in sync as variants are added.
const VARIANT_SPECS: Record<ToastVariant, VariantSpec> = {
  info:    { icon: 'info',         accent: 'accentCerulean', role: 'status' },
  success: { icon: 'check-circle', accent: 'accentGreen',    role: 'status' },
  warning: { icon: 'warning',      accent: 'accentOrange',   role: 'alert'  },
  error:   { icon: 'error',        accent: 'accentCrimson',  role: 'alert'  },
};

/**
 * Renders the active toast queue (up to 3 stacked) inline in the
 * component tree. No portal, no `@tamagui/toast` — just an absolute-
 * positioned column pinned to the bottom of the screen. Per ADR-0077:
 * - Auto-dismiss runs per-toast from `expiresAt`.
 * - Esc dismisses the head-of-queue toast.
 * - Each entry has its own role / aria-live based on variant.
 */
export function AppToastViewport() {
  const queue = useToastStore((s) => s.queue);
  const hide = useToastStore((s) => s.hide);
  const insets = useSafeAreaTokens();

  // Auto-dismiss timer per toast — each entry installs its own timeout.
  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    const now = Date.now();
    for (const toast of queue) {
      if (!toast.expiresAt) continue;
      const ms = toast.expiresAt - now;
      if (ms <= 0) {
        hide(toast.id);
        continue;
      }
      timers.push(setTimeout(() => hide(toast.id), ms));
    }
    return () => {
      for (const t of timers) clearTimeout(t);
    };
  }, [queue, hide]);

  // Esc dismisses the head-of-queue toast — keyboard parity with the
  // close icon. Per ADR-0077. Uses the shared escape-stack so any open
  // overlay (AdaptivePanel / ConfirmDialog / SpeedDial pushed on top of
  // the toast handler) claims Escape first; only when no overlay is
  // open does Esc reach the toast handler.
  useEffect(() => {
    if (queue.length === 0) return;
    return pushEscapeHandler(() => hide());
  }, [queue.length, hide]);

  if (queue.length === 0) return null;

  return (
    <YStack
      position={Platform.OS === 'web' ? ('fixed') : 'absolute'}
      bottom={insets.bottom + 16}
      left={0}
      right={0}
      alignItems="center"
      pointerEvents="box-none"
      zIndex={9999}
      gap={8}
    >
      {queue.map((toast) => (
        <ToastRow
          key={toast.id}
          toast={toast}
          onDismiss={() => hide(toast.id)}
        />
      ))}
    </YStack>
  );
}

function ToastRow({ toast, onDismiss }: { toast: ActiveToast; onDismiss: () => void }) {
  const theme = useThemeColors();
  const spec = VARIANT_SPECS[toast.variant];
  const accent = theme[spec.accent] as string;
  const iconName = spec.icon;
  const role = spec.role;
  const liveRegion = role === 'alert' ? 'assertive' : 'polite';
  const shadow = getPolaroidShadowStyles(theme.polaroidShadow, 'lg');

  return (
    <YStack
      accessibilityRole={role === 'alert' ? 'alert' : 'text'}
      accessibilityLiveRegion={liveRegion}
      role={role}
      aria-live={liveRegion}
      paddingHorizontal={14}
      paddingVertical={12}
      borderRadius={14}
      borderWidth={1}
      borderColor={accent + '33'}
      backgroundColor="$surfaceElevated"
      {...shadow}
      maxWidth={420}
      minWidth={240}
      transition="quick"
      enterStyle={{ opacity: 0, y: 20 }}
      exitStyle={{ opacity: 0, y: 20 }}
      opacity={1}
      y={0}
    >
      {toast.content ? (
        /* Custom-content toasts manage their own dismiss UI and call
           useAppToast().hide() — see SuggestionToastContent. */
        toast.content
      ) : (
        <XStack gap={10} alignItems="flex-start">
          <Icon name={iconName} size={20} color={accent} />
          <YStack flex={1} gap={2}>
            {toast.title ? (
              <Text fontSize={14} fontWeight="700" color="$textOnSurface">
                {toast.title}
              </Text>
            ) : null}
            {toast.description ? (
              <Text fontSize={13} color="$textSubdued">
                {toast.description}
              </Text>
            ) : null}
          </YStack>
          {toast.action ? (
            <Pressable
              onPress={toast.action.onPress}
              hitSlop={12}
              accessibilityRole="button"
              accessibilityLabel={toast.action.label}
              style={{ paddingHorizontal: 4, paddingVertical: 2 }}
            >
              <Text fontSize={13} fontWeight="700" color={accent}>
                {toast.action.label}
              </Text>
            </Pressable>
          ) : null}
          <IconButton
            iconName="close"
            onPress={onDismiss}
            accessibilityLabel="Dismiss notification"
            size="sm"
            tone="subdued"
          />
        </XStack>
      )}
    </YStack>
  );
}
